import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './config';

/**
 * Player Profile Structure:
 * {
 *   odUserId: string,           // Firebase Auth UID
 *   playerId: string,           // Unique 6-digit player ID for easy identification
 *   displayName: string,
 *   displayNameLower: string,   // Lowercase version for case-insensitive search
 *   photoURL: string,
 *
 *   // External ratings (optional, user-entered)
 *   fargoId: string,
 *   fargoRating: number,
 *   apaSkillLevel8Ball: number,
 *   apaSkillLevel9Ball: number,
 *
 *   // Platform stats (auto-calculated)
 *   stats: {
 *     tournamentsPlayed: number,
 *     tournamentWins: number,
 *     secondPlace: number,
 *     thirdPlace: number,
 *     matchesWon: number,
 *     matchesLost: number,
 *     currentStreak: number,    // positive = wins, negative = losses
 *     bestWinStreak: number,
 *     lastUpdated: Timestamp
 *   },
 *
 *   // Tournament history (array of results)
 *   tournamentHistory: [
 *     {
 *       tournamentId: string,
 *       tournamentTitle: string,
 *       instanceDate: string,   // ISO string for recurring tournaments
 *       date: Timestamp,
 *       placement: number,      // 1, 2, 3, etc.
 *       totalPlayers: number,
 *       format: string,
 *       matchesWon: number,
 *       matchesLost: number
 *     }
 *   ],
 *
 *   // Head-to-head records
 *   headToHead: {
 *     [opponentId]: { odUserId: string, displayName: string, wins: number, losses: number }
 *   },
 *
 *   createdAt: Timestamp,
 *   lastActive: Timestamp
 * }
 */

// Generate a unique 6-digit player ID
const generatePlayerId = async () => {
  const maxAttempts = 10;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    // Generate a random 6-digit number (100000-999999)
    const playerId = String(Math.floor(100000 + Math.random() * 900000));

    // Check if this ID already exists
    const existingQuery = query(
      collection(db, 'players'),
      where('playerId', '==', playerId),
      limit(1)
    );
    const existingSnap = await getDocs(existingQuery);

    if (existingSnap.empty) {
      return playerId;
    }
  }

  // Fallback: use timestamp-based ID if random generation fails
  return String(Date.now()).slice(-6);
};

// Get a player profile by user ID
export const getPlayerProfile = async (userId) => {
  try {
    const playerRef = doc(db, 'players', userId);
    const playerSnap = await getDoc(playerRef);

    if (playerSnap.exists()) {
      return { id: playerSnap.id, ...playerSnap.data() };
    }

    return null;
  } catch (error) {
    console.error('Error getting player profile:', error);
    throw error;
  }
};

// Create or update a player profile
export const createOrUpdatePlayerProfile = async (userId, profileData) => {
  try {
    const playerRef = doc(db, 'players', userId);
    const playerSnap = await getDoc(playerRef);

    // Get display name and create lowercase version for case-insensitive search
    const displayName = profileData.displayName || 'Unknown Player';
    const displayNameLower = displayName.toLowerCase();

    if (playerSnap.exists()) {
      // Update existing profile
      await updateDoc(playerRef, {
        ...profileData,
        displayNameLower: profileData.displayName ? profileData.displayName.toLowerCase() : undefined,
        lastActive: serverTimestamp()
      });
    } else {
      // Generate a unique player ID for new profiles
      const playerId = await generatePlayerId();

      // Create new profile with default stats
      await setDoc(playerRef, {
        odUserId: userId,
        playerId: playerId,
        displayName: displayName,
        displayNameLower: displayNameLower,
        photoURL: profileData.photoURL || null,
        fargoId: null,
        fargoRating: null,
        apaSkillLevel8Ball: null,
        apaSkillLevel9Ball: null,
        stats: {
          tournamentsPlayed: 0,
          tournamentWins: 0,
          secondPlace: 0,
          thirdPlace: 0,
          matchesWon: 0,
          matchesLost: 0,
          currentStreak: 0,
          bestWinStreak: 0,
          lastUpdated: serverTimestamp()
        },
        tournamentHistory: [],
        headToHead: {},
        createdAt: serverTimestamp(),
        lastActive: serverTimestamp(),
        ...profileData
      });
    }

    // Return the updated profile
    const updatedSnap = await getDoc(playerRef);
    return { id: updatedSnap.id, ...updatedSnap.data() };
  } catch (error) {
    console.error('Error creating/updating player profile:', error);
    throw error;
  }
};

// Update player stats after a tournament
export const updatePlayerStats = async (userId, tournamentResult) => {
  try {
    const playerRef = doc(db, 'players', userId);
    const playerSnap = await getDoc(playerRef);

    if (!playerSnap.exists()) {
      console.error('Player profile not found for stats update');
      return null;
    }

    const currentData = playerSnap.data();
    const currentStats = currentData.stats || {};
    const currentHistory = currentData.tournamentHistory || [];

    // Calculate new stats
    const newTournamentsPlayed = (currentStats.tournamentsPlayed || 0) + 1;
    const newTournamentWins = (currentStats.tournamentWins || 0) + (tournamentResult.placement === 1 ? 1 : 0);
    const newSecondPlace = (currentStats.secondPlace || 0) + (tournamentResult.placement === 2 ? 1 : 0);
    const newThirdPlace = (currentStats.thirdPlace || 0) + (tournamentResult.placement === 3 ? 1 : 0);
    const newMatchesWon = (currentStats.matchesWon || 0) + (tournamentResult.matchesWon || 0);
    const newMatchesLost = (currentStats.matchesLost || 0) + (tournamentResult.matchesLost || 0);

    // Calculate streak (simplified - would need match-by-match data for accuracy)
    let newCurrentStreak = currentStats.currentStreak || 0;
    if (tournamentResult.placement === 1) {
      newCurrentStreak = newCurrentStreak > 0 ? newCurrentStreak + 1 : 1;
    } else {
      newCurrentStreak = 0; // Reset on non-win (simplified)
    }

    const newBestWinStreak = Math.max(currentStats.bestWinStreak || 0, newCurrentStreak);

    // Add to tournament history
    const newHistoryEntry = {
      tournamentId: tournamentResult.tournamentId,
      tournamentTitle: tournamentResult.tournamentTitle,
      instanceDate: tournamentResult.instanceDate || null,
      date: tournamentResult.date || new Date().toISOString(),
      placement: tournamentResult.placement,
      totalPlayers: tournamentResult.totalPlayers,
      format: tournamentResult.format,
      matchesWon: tournamentResult.matchesWon || 0,
      matchesLost: tournamentResult.matchesLost || 0
    };

    // Keep history sorted by date, most recent first
    const newHistory = [newHistoryEntry, ...currentHistory];

    // Update the profile
    await updateDoc(playerRef, {
      stats: {
        tournamentsPlayed: newTournamentsPlayed,
        tournamentWins: newTournamentWins,
        secondPlace: newSecondPlace,
        thirdPlace: newThirdPlace,
        matchesWon: newMatchesWon,
        matchesLost: newMatchesLost,
        currentStreak: newCurrentStreak,
        bestWinStreak: newBestWinStreak,
        lastUpdated: serverTimestamp()
      },
      tournamentHistory: newHistory,
      lastActive: serverTimestamp()
    });

    const updatedSnap = await getDoc(playerRef);
    return { id: updatedSnap.id, ...updatedSnap.data() };
  } catch (error) {
    console.error('Error updating player stats:', error);
    throw error;
  }
};

// Update head-to-head record between two players
export const updateHeadToHead = async (userId, opponentId, opponentName, won) => {
  try {
    const playerRef = doc(db, 'players', userId);
    const playerSnap = await getDoc(playerRef);

    if (!playerSnap.exists()) {
      console.error('Player profile not found for head-to-head update');
      return null;
    }

    const currentData = playerSnap.data();
    const currentH2H = currentData.headToHead || {};

    // Get or create opponent record
    const opponentRecord = currentH2H[opponentId] || {
      odUserId: opponentId,
      displayName: opponentName,
      wins: 0,
      losses: 0
    };

    // Update record
    if (won) {
      opponentRecord.wins = (opponentRecord.wins || 0) + 1;
    } else {
      opponentRecord.losses = (opponentRecord.losses || 0) + 1;
    }

    // Update the profile
    await updateDoc(playerRef, {
      headToHead: {
        ...currentH2H,
        [opponentId]: opponentRecord
      }
    });

    return opponentRecord;
  } catch (error) {
    console.error('Error updating head-to-head:', error);
    throw error;
  }
};

// Get player leaderboard (top players by tournament wins)
export const getLeaderboard = async (limitCount = 20) => {
  try {
    const playersQuery = query(
      collection(db, 'players'),
      orderBy('stats.tournamentWins', 'desc'),
      limit(limitCount)
    );

    const snapshot = await getDocs(playersQuery);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting leaderboard:', error);
    throw error;
  }
};

// Minimum ranked matches before a player appears on the public leaderboard.
// Keep in sync with MIN_RANKED_GAMES in functions/leaderboardEligibility.js.
export const RANKED_MIN_GAMES = 5;

// Ranked win percentage from a rankedStats tally (0-100, one decimal).
export const rankedWinPercentage = (rankedStats) => {
  if (!rankedStats) return 0;
  const total = (rankedStats.matchesWon || 0) + (rankedStats.matchesLost || 0);
  if (total === 0) return 0;
  return Math.round(((rankedStats.matchesWon || 0) / total) * 1000) / 10;
};

// Public leaderboard. Ranks by tournament wins, then win %, among players with at
// least RANKED_MIN_GAMES matches. Only results from leaderboard-eligible tournaments
// (>=8 players, >=60 min) contribute to `stats` — enforced server-side in the stats
// recorder (functions/playerStats.js). `stats` is the single source of truth (the
// old ranked/casual split was removed).
export const getRankedLeaderboard = async (limitCount = 20) => {
  try {
    // Single-field orderBy is auto-indexed. Over-fetch, then filter + tiebreak client-side.
    const playersQuery = query(
      collection(db, 'players'),
      orderBy('stats.tournamentWins', 'desc'),
      limit(Math.max(limitCount * 5, 100))
    );
    const snapshot = await getDocs(playersQuery);
    const players = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    return players
      .filter(p => {
        // Only players who have won at least one tournament appear on the board.
        const s = p.stats || {};
        return (s.tournamentWins || 0) >= 1;
      })
      .sort((a, b) => {
        const aw = a.stats?.tournamentWins || 0;
        const bw = b.stats?.tournamentWins || 0;
        if (bw !== aw) return bw - aw;
        return rankedWinPercentage(b.stats) - rankedWinPercentage(a.stats);
      })
      .slice(0, limitCount);
  } catch (error) {
    console.error('Error getting leaderboard:', error);
    throw error;
  }
};

// Search players by display name (case-insensitive) or player ID
export const searchPlayers = async (searchTerm, limitCount = 10) => {
  try {
    const trimmedTerm = searchTerm.trim();

    // Check if search term looks like a player ID (all digits, 6 characters)
    const isPlayerIdSearch = /^\d{6}$/.test(trimmedTerm);

    if (isPlayerIdSearch) {
      // Search by exact player ID
      const playerIdQuery = query(
        collection(db, 'players'),
        where('playerId', '==', trimmedTerm),
        limit(1)
      );
      const playerIdSnapshot = await getDocs(playerIdQuery);

      if (!playerIdSnapshot.empty) {
        return playerIdSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      }
    }

    // Convert search term to lowercase for case-insensitive name search
    const lowerSearchTerm = trimmedTerm.toLowerCase();

    // Try searching by displayNameLower first (case-insensitive)
    let playersQuery = query(
      collection(db, 'players'),
      where('displayNameLower', '>=', lowerSearchTerm),
      where('displayNameLower', '<=', lowerSearchTerm + '\uf8ff'),
      limit(limitCount)
    );

    let snapshot = await getDocs(playersQuery);

    // If no results with displayNameLower, fall back to displayName (for older profiles)
    if (snapshot.empty) {
      playersQuery = query(
        collection(db, 'players'),
        where('displayName', '>=', searchTerm),
        where('displayName', '<=', searchTerm + '\uf8ff'),
        limit(limitCount)
      );
      snapshot = await getDocs(playersQuery);
    }

    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error searching players:', error);
    throw error;
  }
};

// Calculate win percentage
export const calculateWinPercentage = (stats) => {
  if (!stats) return 0;
  const totalMatches = (stats.matchesWon || 0) + (stats.matchesLost || 0);
  if (totalMatches === 0) return 0;
  return Math.round(((stats.matchesWon || 0) / totalMatches) * 100 * 10) / 10;
};

// Get player's recent form (last N tournaments)
export const getRecentForm = (tournamentHistory, count = 5) => {
  if (!tournamentHistory || tournamentHistory.length === 0) return [];
  return tournamentHistory.slice(0, count).map(t => ({
    placement: t.placement,
    totalPlayers: t.totalPlayers,
    isWin: t.placement === 1,
    isPodium: t.placement <= 3
  }));
};

// Initialize profile for existing user (call when user first accesses profile features)
export const initializePlayerProfile = async (user) => {
  if (!user || !user.uid) return null;

  try {
    const existingProfile = await getPlayerProfile(user.uid);

    if (existingProfile) {
      // Update last active and sync display name/photo if changed
      const displayName = user.displayName || existingProfile.displayName;
      const updateData = {
        displayName: displayName,
        displayNameLower: displayName.toLowerCase(),
        photoURL: user.photoURL || existingProfile.photoURL,
        lastActive: serverTimestamp()
      };

      // If existing profile doesn't have a playerId, generate one
      if (!existingProfile.playerId) {
        updateData.playerId = await generatePlayerId();
      }

      await updateDoc(doc(db, 'players', user.uid), updateData);
      return await getPlayerProfile(user.uid);
    }

    // Create new profile
    return await createOrUpdatePlayerProfile(user.uid, {
      displayName: user.displayName || 'Unknown Player',
      photoURL: user.photoURL || null
    });
  } catch (error) {
    console.error('Error initializing player profile:', error);
    throw error;
  }
};

// Backfill Player IDs for all existing profiles that don't have one
// This is a one-time migration function - call from admin or console
export const backfillPlayerIds = async () => {
  try {
    // Query all players without a playerId
    const playersRef = collection(db, 'players');
    const snapshot = await getDocs(playersRef);

    let updatedCount = 0;
    const updates = [];

    for (const playerDoc of snapshot.docs) {
      const data = playerDoc.data();
      if (!data.playerId) {
        const newPlayerId = await generatePlayerId();
        updates.push({
          ref: playerDoc.ref,
          playerId: newPlayerId,
          displayName: data.displayName
        });
      }
    }

    // Apply updates
    for (const update of updates) {
      await updateDoc(update.ref, { playerId: update.playerId });
      updatedCount++;
      console.log(`Assigned Player ID ${update.playerId} to ${update.displayName}`);
    }

    console.log(`Backfill complete: ${updatedCount} profiles updated`);
    return { success: true, updatedCount };
  } catch (error) {
    console.error('Error backfilling player IDs:', error);
    throw error;
  }
};
